import { useState } from 'react';
import { motion } from 'framer-motion';
import ReactPlayer from 'react-player/youtube.js';
import { TrendingUp, TrendingDown, Trophy, Check, X as XIcon } from 'lucide-react';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogClose,
} from '@/components/ui/dialog';
import {
	Select,
	SelectTrigger,
	SelectValue,
	SelectContent,
	SelectItem,
} from '@/components/ui/select';
import { cn } from '@/utils';

const TOPICS = [
	{ key: 'basics', label: 'Learn the Basics' },
	{ key: 'markets', label: 'What is a market?' },
	{ key: 'trading', label: 'How do I make a prediction?' },
	{ key: 'settlement', label: 'How are markets settled?' },
	{ key: 'scoring', label: 'How do I score?' },
];

function Section({ title, icon, children }) {
	return (
		<motion.div
			initial={{ opacity: 0, y: 8 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.25 }}
			className="rounded-xl sm:p-6"
		>
			<h2 className="text-base font-bold font-semibold text-[#F7F7F8] mb-3 flex items-center gap-2">
				{icon}
				{title}
			</h2>
			{children}
		</motion.div>
	);
}

function InfoCard({ title, children }) {
	return (
		<div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700 mb-4 last:mb-0">
			{title && <h4 className="text-white font-medium mb-2 text-sm sm:text-base">{title}</h4>}
			<div className="text-sm font-normal text-[#D4D5D8]">{children}</div>
		</div>
	);
}

function HLCard({ tone, title, icon, children }) {
	const toneCls =
		tone === 'green' ? 'bg-green-900/20 border-green-700/30' : 'bg-red-900/20 border-red-700/30';
	return (
		<div className={`rounded-lg p-4 border ${toneCls} mb-4 last:mb-0`}>
			{(title || icon) && (
				<div className="flex items-center gap-2 mb-2">
					{icon}
					{title && <h4 className="text-white font-medium text-sm sm:text-base">{title}</h4>}
				</div>
			)}
			<div className="text-sm font-normal text-[#D4D5D8]">{children}</div>
		</div>
	);
}

export default function HowToPlayModal({ open, onOpenChange }) {
	const [topic, setTopic] = useState('basics');

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				hideCancelIcon
				className="
          w-[92vw] max-w-[628px]
          h-[90vh] max-h-[824px]
          bg-[#101423] border border-[#1E2748] rounded-lg p-0
          backdrop-blur-xl flex flex-col overflow-hidden gap-0
        "
			>
				<DialogClose
					className="w-8 h-8 absolute right-3 top-3 rounded-full text-white/60"
					aria-label="Close"
				>
					<XIcon className="w-6 h-6 text-[#949599]" />
				</DialogClose>

				{/* Header (fixed) */}
				<DialogHeader className="px-6 pt-6 gap-6">
					<div className="flex items-center justify-between">
						<div className="w-6" />
						<DialogTitle className="text-center text-white text-xl font-semibold">
							How to Play
						</DialogTitle>
						<div />
					</div>

					{/* Topic dropdown */}
					<div>
						<Select value={topic} onValueChange={(v) => setTopic(v)}>
							<SelectTrigger
								iconSize={20}
								iconColor="#F7F7F8"
								className="w-full px-4 bg-[#171E36] text-white/90 text-sm font-bold border border-[#2D3A6C] rounded-3xl"
							>
								<SelectValue placeholder="Topic" />
							</SelectTrigger>

							<SelectContent
								className="
                  bg-[#171E36]
                  border border-[#2D3A6C]
                  rounded-3xl
                  overflow-hidden
                  shadow-lg
                  text-sm
                "
							>
								{TOPICS.map((t, i) => (
									<SelectItem
										key={t.key}
										value={t.key}
										transparentHover
										className={cn(
											`
                        cursor-pointer
                        hover:bg-white/10
                        hover:text-[#81A1F8]
                        relative px-4 py-2
                        border-[#2D3A6C] border-b
                        text-[#F7F7F8]
                        transition-colors
                        last:border-0
                        data-[state=checked]:text-[#81A1F8]
                      `,
											i === TOPICS.length - 1 && 'border-b border-[#171E36]',
											topic === t.key && 'text-[#81A1F8]'
										)}
									>
										{t.label}
										<style jsx>{`
											[data-state='checked'] svg {
												display: none;
											}
										`}</style>
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</DialogHeader>

				{/* Body (scrolls) */}
				<div className="flex-1 overflow-y-auto pb-4">
					{topic === 'basics' && (
						<motion.div
							initial={{ opacity: 0, y: 8 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.25 }}
							className="rounded-xl p-4 sm:p-6"
						>
							<h2 className="text-base font-bold text-[#F7F7F8] leading-5 mb-2">Explainer Video</h2>
							<p className="text-sm font-normal text-[#D4D5D8] leading-relaxed mb-4">
								Watch this quick explainer to see how the game works—place your picks, climb the
								leaderboard, and see who’s the best!
							</p>
							<div className="aspect-video w-full overflow-hidden rounded-lg">
								<ReactPlayer
									url="https://www.youtube.com/watch?v=HA1sADgDlTI"
									width="100%"
									height="100%"
									controls
								/>
							</div>
						</motion.div>
					)}

					{topic === 'markets' && (
						<Section
							title="The Markets & The Price"
							icon={<TrendingUp className="h-5 w-5 text-[#2667FF]" />}
						>
							<p className="text-sm font-normal text-[#D4D5D8] mb-4">
								Think of each question as its own mini-market. Each market has a Price from 0 to
								100.
							</p>
							<InfoCard title="What the Price means:">
								It’s the market’s belief about the probability of the event. A price of 65 suggests
								~65% chance it happens.
							</InfoCard>
							<InfoCard title="How the Price changes:">
								Prices move with everyone’s trading. More “will happen” → price rises; more “won’t
								happen” → price falls.
							</InfoCard>
						</Section>
					)}

					{topic === 'trading' && (
						<Section
							title="Trading: Go Long or Go Short?"
							icon={
								<div className="flex gap-2">
									<TrendingUp className="h-5 w-5" />
									<TrendingDown className="h-5 w-5" />
								</div>
							}
						>
							<p className="text-sm font-normal text-[#D4D5D8] mb-4">
								Trade each market once: choose if the event WILL or WILL NOT happen.
							</p>
							<HLCard
								tone="green"
								title="Go LONG (Buy Shares)"
								icon={<TrendingUp className="h-4 w-4" />}
							>
								Believe it WILL happen → you want price to move{' '}
								<span className="text-green-400 font-semibold">up toward 100</span>.
							</HLCard>
							<HLCard
								tone="red"
								title="Go SHORT (Sell Shares)"
								icon={<TrendingDown className="h-4 w-4" />}
							>
								Believe it WILL NOT happen → you want price to move{' '}
								<span className="text-red-400 font-semibold">down toward 0</span>.
							</HLCard>
						</Section>
					)}

					{topic === 'settlement' && (
						<Section
							title="Settlement: How Markets Close"
							icon={<Check className="h-5 w-5 text-green-500" />}
						>
							<HLCard tone="green" icon={<Check className="h-5 w-5" />}>
								If the event DID happen → market settles at{' '}
								<span className="text-green-400 font-semibold">100</span>.
							</HLCard>
							<HLCard tone="red" icon={<XIcon className="h-5 w-5" />}>
								If the event DID NOT happen → market settles at{' '}
								<span className="text-red-400 font-semibold">0</span>.
							</HLCard>
						</Section>
					)}

					{topic === 'scoring' && (
						<Section
							title="Profit & Loss: How You Score Points"
							icon={<Trophy className="h-5 w-5 text-amber-500" />}
						>
							<p className="text-sm font-normal text-[#D4D5D8] mb-4">
								Points depend on your direction and the difference between your trade price and the
								final settlement (100 or 0).
							</p>
							<InfoCard title="If you went LONG (Bought) at Price X:">
								<ul className="space-y-2 text-sm font-normal text-[#D4D5D8]">
									<li className="flex items-start gap-2">
										<Check className="h-4 w-4 text-green-500 mt-1" />
										<span>If settles at 100 → Profit = 100 − X</span>
									</li>
									<li className="flex items-start gap-2">
										<XIcon className="h-4 w-4 text-red-500 mt-1" />
										<span>If settles at 0 → Loss = 0 − X (you lose X)</span>
									</li>
								</ul>
							</InfoCard>
							<InfoCard title="If you went SHORT (Sold) at Price Y:">
								<ul className="space-y-2 text-sm font-normal text-[#D4D5D8]">
									<li className="flex items-start gap-2">
										<Check className="h-4 w-4 text-green-500 mt-1" />
										<span>If settles at 0 → Profit = Y</span>
									</li>
									<li className="flex items-start gap-2">
										<XIcon className="h-4 w-4 text-red-500 mt-1" />
										<span>If settles at 100 → Loss = 100 − Y</span>
									</li>
								</ul>
							</InfoCard>
						</Section>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
}
